<script setup lang="ts">
import type {
  TApplianceCountByProgrammingCategory,
  TSectionApplianceCategory,
} from "#/ui-kit/components/section/appliances/section-appliance-category.types";

import {
  categoryOrder,
  EOtherApplianceCategory,
  PROGRAM_APPLIANCE_CATEGORY,
} from "#/ui-kit/components/section/appliances/section-appliance-category.types";
import SectionApplianceIcon from "#/ui-kit/components/section/appliances/section-appliance-icon.vue";
import SectionApplianceText from "#/ui-kit/components/section/appliances/section-appliance-text.vue";
import { useCommonFrontendIntl } from "#/ui-kit/locales/typed-intl";
import { DATA_TEST_IDS, isDefined } from "@voltalis/frontend-utils";
import { sum } from "es-toolkit";
import { computed } from "vue";

interface IProps {
  applianceCountByProgrammingCategory?: TApplianceCountByProgrammingCategory;
  isMoveAppliancesEnabled?: boolean;
}

interface IEmits {
  (e: "move-appliances"): void;
  (e: "see-all-appliances"): void;
}

const props = defineProps<IProps>();
const emits = defineEmits<IEmits>();

const { t, k } = useCommonFrontendIntl();

const appliancesTotalCount = computed<number>(() => {
  return sum(Object.values(props.applianceCountByProgrammingCategory ?? {}).filter((n) => isDefined(n)));
});

const summedCategories = computed<Record<TSectionApplianceCategory, number>>(() => {
  const result: Record<TSectionApplianceCategory, number> = {
    [PROGRAM_APPLIANCE_CATEGORY.HEATER]: 0,
    [PROGRAM_APPLIANCE_CATEGORY.WATER_HEATER]: 0,
    [PROGRAM_APPLIANCE_CATEGORY.AC_UNIT_NON_INFRARED]: 0,
    [EOtherApplianceCategory.OTHER]: 0,
  };

  for (const [category, maybeCategoryCount] of Object.entries(props.applianceCountByProgrammingCategory ?? {})) {
    const categoryCount = maybeCategoryCount ?? 0;

    switch (category) {
      case PROGRAM_APPLIANCE_CATEGORY.HEATER:
        result[PROGRAM_APPLIANCE_CATEGORY.HEATER] = categoryCount;
        break;
      case PROGRAM_APPLIANCE_CATEGORY.WATER_HEATER:
        result[PROGRAM_APPLIANCE_CATEGORY.WATER_HEATER] = categoryCount;
        break;
      case PROGRAM_APPLIANCE_CATEGORY.AC_UNIT_NON_INFRARED:
        result[PROGRAM_APPLIANCE_CATEGORY.AC_UNIT_NON_INFRARED] = categoryCount;
        break;
      default:
        result[EOtherApplianceCategory.OTHER] += categoryCount;
    }
  }

  return result;
});

function getMaybeCategoryValue(category: TSectionApplianceCategory): number | undefined {
  return summedCategories.value[category];
}
</script>

<template>
  <div class="flex justify-between items-center">
    <small
      class="uppercase text-gray-600"
      :data-test-id="DATA_TEST_IDS.components.siteSectionAppliancesSummary.title"
    >
      {{ t(k.commonFrontend["site-sections-tree"]["section-appliances"].title) }}
    </small>

    <button
      v-if="props.isMoveAppliancesEnabled && appliancesTotalCount"
      class="text-brand flex items-center gap-2"
      @click="emits('move-appliances')"
      :data-test-id="DATA_TEST_IDS.components.siteSectionAppliancesSummary.moveAppliancesButton"
    >
      <i class="pi pi-pen-to-square" />
      <span class="font-bold">
        {{ t(k.commonFrontend["site-sections-tree"]["section-appliances"]["move-appliances"]) }}
      </span>
    </button>
  </div>

  <div
    v-if="!appliancesTotalCount"
    class="no-appliance"
    :data-test-id="DATA_TEST_IDS.components.siteSectionAppliancesSummary.noApplianceText"
  >
    <p class="italic text-gray-500">
      {{
        t(
          k.commonFrontend["site-sections-tree"]["appliance-count"]({
            count: 0,
          }),
        )
      }}
    </p>
  </div>

  <div
    v-else
    class="border rounded-2xl p-4"
  >
    <div class="flex flex-col gap-2">
      <template
        v-for="category in categoryOrder"
        :key="category"
      >
        <template v-if="getMaybeCategoryValue(category)">
          <div
            class="appliance-count-row"
            :data-test-id="DATA_TEST_IDS.components.siteSectionAppliancesSummary.categoryItem"
          >
            <div :data-test-id="DATA_TEST_IDS.components.siteSectionAppliancesSummary.categoryItemIcon">
              <SectionApplianceIcon
                size="large"
                :sectionApplianceCategory="category"
              />
            </div>
            <div class="flex flex-col">
              <SectionApplianceText
                class="font-medium"
                :appliance-category="category"
              />
              <small
                class="text-gray-500 text-xs"
                :data-test-id="DATA_TEST_IDS.components.siteSectionAppliancesSummary.categoryItemCount"
                >{{
                  t(
                    k.commonFrontend["site-sections-tree"]["appliance-count"]({
                      count: getMaybeCategoryValue(category) ?? 0,
                    }),
                  )
                }}</small
              >
            </div>
          </div>
        </template>
      </template>
    </div>
    <button
      v-if="props.isMoveAppliancesEnabled && appliancesTotalCount"
      :data-test-id="DATA_TEST_IDS.components.siteSectionAppliancesSummary.seeAllButton"
      class="flex justify-between items-center w-full font-bold text-brand pt-2"
      @click="emits('see-all-appliances')"
    >
      <span
        class="font-semibold"
        :data-test-id="DATA_TEST_IDS.components.siteSectionAppliancesSummary.totalCount"
      >
        {{
          t(
            k.commonFrontend["site-sections-tree"]["section-appliances"]["see-all"]({
              count: appliancesTotalCount,
            }),
          )
        }}
      </span>
      <i class="pi pi-arrow-right" />
    </button>
  </div>
</template>

<style scoped lang="scss">
.appliance-count-row {
  @apply flex items-center gap-2 pb-2;
  height: 45px;

  &:not(:last-of-type) {
    @apply border-b;
  }
}

.no-appliance {
  @apply border rounded-2xl py-8 px-4 border-dashed;
}
</style>
